// =============================================================================
// Origin UI — src/pages/offers/OfferDetailPage.tsx
// Full detail view for a single offer with editable fields, status actions,
// audit timeline, notes, and document upload/download.
// Batch 1.6 + 1.7 — Onboarding Lifecycle module.
// =============================================================================

import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { offerApi, OfferDetail, UpdateOfferDto, OfferDocumentDto, OFFER_DOC_CATEGORIES } from '@/services/offerApi';
import { api } from '@/services/apiService';
import styles from './OfferDetailPage.module.css';

// ─── Types ───

interface AuditEvent {
  auditEventId: string;
  action: string;
  performedBy: string;
  performedByDisplayName: string;
  timestamp: string;
  details: string | null;
}

interface Department {
  departmentId: number;
  name: string;
}

interface OfficeLocation {
  officeLocationId: number;
  name: string;
}

interface ManagerOption {
  employeeId: string;
  name: string;
}

// ─── Status helpers ───

const STATUS_COLOURS: Record<string, string> = {
  Draft: '#6c757d',
  Sent: '#0d6efd',
  Accepted: '#198754',
  Declined: '#dc3545',
  Expired: '#ffc107',
  Withdrawn: '#adb5bd',
  Onboarding: '#00B0B9',
};

function getStatusActions(status: string): { label: string; newStatus: string; variant: 'primary' | 'danger' | 'success' }[] {
  switch (status) {
    case 'Draft':
      return [
        { label: 'Mark as Sent', newStatus: 'Sent', variant: 'primary' },
        { label: 'Withdraw', newStatus: 'Withdrawn', variant: 'danger' },
      ];
    case 'Sent':
      return [
        { label: 'Record Acceptance', newStatus: 'Accepted', variant: 'success' },
        { label: 'Record Decline', newStatus: 'Declined', variant: 'danger' },
        { label: 'Mark Expired', newStatus: 'Expired', variant: 'danger' },
        { label: 'Withdraw', newStatus: 'Withdrawn', variant: 'danger' },
      ];
    default:
      return [];
  }
}

function isEditable(status: string): boolean {
  return status === 'Draft' || status === 'Sent';
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateTime(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return `${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

function formatCurrency(amount: number | null | undefined, currency: string | null | undefined): string {
  if (amount == null) return '—';
  const formatted = amount.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  return currency ? `${currency} ${formatted}` : formatted;
}

// ─── Component ───

export function OfferDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [offer, setOffer] = useState<OfferDetail | null>(null);
  const [loading, setLoading] = useState(id !== 'new' && !!id);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(id === 'new' || !id);
  const [statusChanging, setStatusChanging] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{ label: string; newStatus: string } | null>(null);
  const [statusRemarks, setStatusRemarks] = useState('');

  // Document state
  const [uploading, setUploading] = useState(false);
  const [uploadCategory, setUploadCategory] = useState<string>('Other');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Checklist state (WS-4)
  const [checklistItems, setChecklistItems] = useState<any[]>([]);
  const [, setChecklistLoading] = useState(false);

  // Edit form state
  const [form, setForm] = useState<UpdateOfferDto>({
    candidateName: '',
    candidateEmail: '',
    jobTitle: '',
    departmentId: 0,
    officeLocationId: 0,
    lineManagerId: null,
    employmentType: '',
    proposedStartDate: null,
    proposedSalary: null,
    salaryCurrency: null,
    offerExpiryDate: null,
    notes: null,
    visaRequired: false,
    visaType: null,
    visaStatus: null,
    visaNotes: null,
  });

  // Lookup data
  const [departments, setDepartments] = useState<Department[]>([]);
  const [offices, setOffices] = useState<OfficeLocation[]>([]);
  const [managers, setManagers] = useState<ManagerOption[]>([]);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>([]);

  // ─── Loaders ───

  const isCreateMode = !id || id === "new";

  const loadOffer = useCallback(async () => {
    if (!id) return;
    try {
      if (isCreateMode) { setLoading(false); setEditing(true); return; }
      setLoading(true);
      const data = await offerApi.getOffer(id);
      setOffer(data);
      setForm({
        candidateName: data.candidateName,
        candidateEmail: data.candidateEmail,
        jobTitle: data.jobTitle,
        departmentId: data.departmentId,
        officeLocationId: data.officeLocationId,
        lineManagerId: data.lineManagerId ?? null,
        employmentType: data.employmentType,
        proposedStartDate: data.proposedStartDate ?? null,
        proposedSalary: data.proposedSalary ?? null,
        salaryCurrency: data.salaryCurrency ?? null,
        offerExpiryDate: data.offerExpiryDate ?? null,
        notes: data.notes ?? null,
        visaRequired: data.visaRequired,
        visaType: data.visaType ?? null,
        visaStatus: data.visaStatus ?? null,
        visaNotes: data.visaNotes ?? null,
      });
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load offer');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadLookups = useCallback(async () => {
    try {
      const [depts, locs, mgrs] = await Promise.all([
        api.get<Department[]>('/api/reference/departments'),
        api.get<OfficeLocation[]>('/api/reference/office-locations'),
        api.get<ManagerOption[]>('/api/employees/managers'),
      ]);
      setDepartments(depts);
      setOffices(locs);
      setManagers(mgrs);
    } catch {
      // Non-critical
    }
  }, []);

  const loadAuditEvents = useCallback(async () => {
    if (!id || id === "new") return;
    try {
      const events = await api.get<AuditEvent[]>(`/api/audit-events?entityType=Offer&entityId=${id}`);
      setAuditEvents(events);
    } catch {
      // Non-critical
    }
  }, [id]);

  const loadChecklist = useCallback(async () => {
    if (!id || id === 'new') return;
    try {
      setChecklistLoading(true);
      const items = await api.get<any[]>(`/api/offers/${id}/checklist`);
      setChecklistItems(items);
    } catch {
      // Non-critical — offer may not have a checklist
    } finally {
      setChecklistLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadOffer();
    loadLookups();
    loadAuditEvents();
    loadChecklist();
  }, [loadOffer, loadLookups, loadAuditEvents, loadChecklist]);

  // ─── Offer handlers ───

  const handleSave = async () => {
    try {
      setSaving(true);
      if (isCreateMode) {
        const created = await offerApi.createOffer(form);
        navigate("/offers/" + created.offerId, { replace: true });
        return;
      }
      if (!id || !offer) return;
      await offerApi.updateOffer(id, form);
      await loadOffer();
      await loadAuditEvents();
      setEditing(false);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!id) return;
    try {
      setStatusChanging(true);
      await offerApi.changeOfferStatus(id, newStatus, statusRemarks || undefined);
      await loadOffer();
      await loadAuditEvents();
      setConfirmAction(null);
      setStatusRemarks('');
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to update status');
    } finally {
      setStatusChanging(false);
    }
  };

  const handleCancel = () => {
    if (!offer) return;
    setForm({
      candidateName: offer.candidateName,
      candidateEmail: offer.candidateEmail,
      jobTitle: offer.jobTitle,
      departmentId: offer.departmentId,
      officeLocationId: offer.officeLocationId,
      lineManagerId: offer.lineManagerId ?? null,
      employmentType: offer.employmentType,
      proposedStartDate: offer.proposedStartDate ?? null,
      proposedSalary: offer.proposedSalary ?? null,
      salaryCurrency: offer.salaryCurrency ?? null,
      offerExpiryDate: offer.offerExpiryDate ?? null,
      notes: offer.notes ?? null,
      visaRequired: offer.visaRequired,
      visaType: offer.visaType ?? null,
      visaStatus: offer.visaStatus ?? null,
      visaNotes: offer.visaNotes ?? null,
    });
    setEditing(false);
  };

  const updateField = <K extends keyof UpdateOfferDto>(field: K, value: UpdateOfferDto[K]) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // ─── Document handlers ───

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !id) return;
    try {
      setUploading(true);
      await offerApi.uploadDocument(id, file, uploadCategory);
      await loadOffer();
      setUploadCategory('Other');
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc: OfferDocumentDto) => {
    if (!id) return;
    try {
      await offerApi.downloadDocument(id, doc.offerDocumentId, doc.fileName);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Download failed');
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    if (!id) return;
    try {
      await offerApi.deleteDocument(id, documentId);
      await loadOffer();
      setDeleteConfirmId(null);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  // ─── Render helpers ───

  const renderField = (label: string, value: React.ReactNode) => (
    <div className={styles.field}>
      <div className={styles.fieldLabel}>{label}</div>
      <div className={styles.fieldValue}>{value || '—'}</div>
    </div>
  );

  const renderInput = (label: string, value: string, onChange: (v: string) => void, type: string = 'text') => (
    <div className={styles.field}>
      <label className={styles.fieldLabel}>{label}</label>
      <input
        type={type}
        className={styles.input}
        value={value}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  );

  const renderSelect = (label: string, value: string | number, onChange: (v: string) => void, options: { value: string | number; label: string }[]) => (
    <div className={styles.field}>
      <label className={styles.fieldLabel}>{label}</label>
      <select className={styles.select} value={value} onChange={e => onChange(e.target.value)}>
        <option value="">— Select —</option>
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );

  // ─── Loading / Error ───

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading offer details…</div>
      </div>
    );
  }

  if (!isCreateMode && (error || !offer)) {
    return (
      <div className={styles.container}>
        <div className={styles.error}>
          <p>{error || 'Offer not found'}</p>
          <button className={styles.btnSecondary} onClick={() => navigate('/offers')}>Back to Offers</button>
        </div>
      </div>
    );
  }

  const canEdit = isCreateMode || isEditable(offer?.status || "Draft");
  const statusActions = isCreateMode ? [] : getStatusActions(offer?.status || "");
  const o = offer ?? {} as any;
  const deptName = departments.find(d => d.departmentId === (offer?.departmentId || form.departmentId))?.name || o.departmentName;
  const officeName = offices.find(o => o.officeLocationId === (offer?.officeLocationId || form.officeLocationId))?.name || o.officeName;
  const managerName = managers.find(m => m.employeeId === (offer?.lineManagerId || form.lineManagerId))?.name || o.lineManagerName;

  return (
    <div className={styles.container}>
      {/* ─── Header ─── */}
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <button className={styles.backBtn} onClick={() => navigate('/offers')}>← Back</button>
          <div>
            <h1 className={styles.title}>{isCreateMode ? "New Offer" : o.candidateName}</h1>
            <p className={styles.subtitle}>{isCreateMode ? "Create a new offer" : `${o.jobTitle} · ${officeName}`}</p>
          </div>
        </div>
        <div className={styles.headerRight}>
          <span
            className={styles.statusBadge}
            style={{ display: isCreateMode ? "none" : undefined, backgroundColor: STATUS_COLOURS[offer?.status || ""] || '#6c757d' }}
          >
            {o.status}
          </span>
          {!isCreateMode && canEdit && !editing && (
            <button className={styles.btnPrimary} onClick={() => setEditing(true)}>Edit</button>
          )}
          {editing && (
            <>
              <button className={styles.btnPrimary} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button className={styles.btnSecondary} onClick={handleCancel} disabled={saving}>Cancel</button>
            </>
          )}
        </div>
      </div>

      {/* ─── Status Actions ─── */}
      {statusActions.length > 0 && !editing && (
        <div className={styles.actionsBar}>
          {statusActions.map(action => (
            <button
              key={action.newStatus}
              className={action.variant === 'primary' ? styles.btnPrimary : action.variant === 'success' ? styles.btnSuccess : styles.btnDanger}
              onClick={() => setConfirmAction(action)}
              disabled={statusChanging}
            >
              {action.label}
            </button>
          ))}
        </div>
      )}

      {/* ─── Status Confirmation Modal ─── */}
      {confirmAction && (
        <div className={styles.modalOverlay} onClick={() => setConfirmAction(null)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 className={styles.modalTitle}>Confirm: {confirmAction.label}</h3>
            <p className={styles.modalText}>
              This will change the offer status from <strong>{o.status}</strong> to <strong>{confirmAction.newStatus}</strong>.
            </p>
            <div className={styles.field}>
              <label className={styles.fieldLabel}>Remarks (optional)</label>
              <textarea
                className={styles.textarea}
                value={statusRemarks}
                onChange={e => setStatusRemarks(e.target.value)}
                rows={3}
                placeholder="Add any notes about this status change…"
              />
            </div>
            <div className={styles.modalActions}>
              <button
                className={styles.btnPrimary}
                onClick={() => handleStatusChange(confirmAction.newStatus)}
                disabled={statusChanging}
              >
                {statusChanging ? 'Processing…' : 'Confirm'}
              </button>
              <button
                className={styles.btnSecondary}
                onClick={() => { setConfirmAction(null); setStatusRemarks(''); }}
                disabled={statusChanging}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.content}>
        {/* ─── Offer Details Card ─── */}
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Offer Details</h2>

          {editing ? (
            <>
              <div className={styles.fieldGrid}>
                {renderInput('Candidate Name', form.candidateName, v => updateField('candidateName', v))}
                {renderInput('Email', form.candidateEmail, v => updateField('candidateEmail', v), 'email')}
                {renderInput('Job Title', form.jobTitle, v => updateField('jobTitle', v))}
                {renderSelect('Department', form.departmentId, v => updateField('departmentId', parseInt(v)), departments.map(d => ({ value: d.departmentId, label: d.name })))}
                {renderSelect('Office', form.officeLocationId, v => updateField('officeLocationId', parseInt(v)), offices.map(o => ({ value: o.officeLocationId, label: o.name })))}
                {renderSelect('Line Manager', form.lineManagerId || '', v => updateField('lineManagerId', v || null), managers.map(m => ({ value: m.employeeId, label: m.name })))}
                {renderSelect('Employment Type', form.employmentType, v => updateField('employmentType', v), [
                  { value: 'Permanent', label: 'Permanent' },
                  { value: 'Contract', label: 'Contract' },
                  { value: 'Temporary', label: 'Temporary' },
                ])}
                {renderInput('Proposed Start Date', form.proposedStartDate ? new Date(form.proposedStartDate).toISOString().split('T')[0] : '', v => updateField('proposedStartDate', v ? new Date(v).toISOString() : null), 'date')}
              </div>

              <h3 className={styles.sectionSubtitle}>Compensation</h3>
              <div className={styles.fieldGrid}>
                {renderInput('Proposed Salary', form.proposedSalary?.toString() || '', v => updateField('proposedSalary', v ? parseFloat(v) : null), 'number')}
                {renderSelect('Currency', form.salaryCurrency || '', v => updateField('salaryCurrency', v || null), [
                  { value: 'AED', label: 'AED' },
                  { value: 'OMR', label: 'OMR' },
                  { value: 'USD', label: 'USD' },
                  { value: 'GBP', label: 'GBP' },
                  { value: 'SGD', label: 'SGD' },
                  { value: 'EUR', label: 'EUR' },
                  { value: 'CNY', label: 'CNY' },
                ])}
              </div>

              <h3 className={styles.sectionSubtitle}>Offer Timeline</h3>
              <div className={styles.fieldGrid}>
                {renderInput('Offer Expiry Date', form.offerExpiryDate ? new Date(form.offerExpiryDate).toISOString().split('T')[0] : '', v => updateField('offerExpiryDate', v ? new Date(v).toISOString() : null), 'date')}
              </div>

              <h3 className={styles.sectionSubtitle}>Visa</h3>
              <div className={styles.fieldGrid}>
                <div className={styles.field}>
                  <label className={styles.fieldLabel}>Visa Required</label>
                  <label className={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={form.visaRequired}
                      onChange={e => updateField('visaRequired', e.target.checked)}
                    />
                    Yes, visa sponsorship is required
                  </label>
                </div>
                {form.visaRequired && (
                  <>
                    {renderInput('Visa Type', form.visaType || '', v => updateField('visaType', v || null))}
                    {renderSelect('Visa Status', form.visaStatus || '', v => updateField('visaStatus', v || null), [
                      { value: 'NotStarted', label: 'Not Started' },
                      { value: 'InProgress', label: 'In Progress' },
                      { value: 'Approved', label: 'Approved' },
                      { value: 'Rejected', label: 'Rejected' },
                    ])}
                    {renderInput('Visa Notes', form.visaNotes || '', v => updateField('visaNotes', v || null))}
                  </>
                )}
              </div>

              <h3 className={styles.sectionSubtitle}>Notes</h3>
              <div className={styles.field}>
                <textarea
                  className={styles.textarea}
                  value={form.notes || ''}
                  onChange={e => updateField('notes', e.target.value || null)}
                  rows={4}
                  placeholder="Internal notes about this offer…"
                />
              </div>
            </>
          ) : (
            <>
              <div className={styles.fieldGrid}>
                {renderField('Candidate Name', o.candidateName)}
                {renderField('Email', o.candidateEmail)}
                {renderField('Job Title', o.jobTitle)}
                {renderField('Department', deptName)}
                {renderField('Office', officeName)}
                {renderField('Line Manager', managerName)}
                {renderField('Employment Type', o.employmentType)}
                {renderField('Proposed Start Date', formatDate(o.proposedStartDate))}
              </div>

              <h3 className={styles.sectionSubtitle}>Compensation</h3>
              <div className={styles.fieldGrid}>
                {renderField('Proposed Salary', formatCurrency(o.proposedSalary, o.salaryCurrency))}
                {renderField('Currency', o.salaryCurrency)}
              </div>

              <h3 className={styles.sectionSubtitle}>Offer Timeline</h3>
              <div className={styles.fieldGrid}>
                {renderField('Offer Expiry Date', formatDate(o.offerExpiryDate))}
                {renderField('Created', formatDateTime(o.createdAt))}
                {renderField('Last Updated', formatDateTime(o.updatedAt))}
                {renderField('Created By', o.createdBy)}
              </div>

              {o.visaRequired && (
                <>
                  <h3 className={styles.sectionSubtitle}>Visa</h3>
                  <div className={styles.fieldGrid}>
                    {renderField('Visa Required', 'Yes')}
                    {renderField('Visa Type', o.visaType)}
                    {renderField('Visa Status', o.visaStatus)}
                    {renderField('Visa Notes', o.visaNotes)}
                  </div>
                </>
              )}

              {o.onboardingRequestId && (
                <>
                  <h3 className={styles.sectionSubtitle}>Linked Onboarding</h3>
                  <div className={styles.fieldGrid}>
                    <div className={styles.field}>
                      <div className={styles.fieldLabel}>Onboarding Request</div>
                      <div className={styles.fieldValue}>
                        <button
                          className={styles.linkBtn}
                          onClick={() => navigate(`/requests/${o.onboardingRequestId}`)}
                        >
                          View Request →
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {o.notes && (
                <>
                  <h3 className={styles.sectionSubtitle}>Notes</h3>
                  <p className={styles.notesText}>{o.notes}</p>
                </>
              )}
            </>
          )}
        </div>

        {/* ─── Documents Card (Batch 1.7) ─── */}
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>
            Documents
            {o.documents?.length || 0 > 0 && (
              <span className={styles.docCount}>{o.documents?.length || 0}</span>
            )}
          </h2>

          {/* Upload area — only when offer is not in a terminal state */}
          {canEdit && (
            <div className={styles.uploadArea}>
              <div className={styles.uploadRow}>
                <select
                  className={styles.select}
                  value={uploadCategory}
                  onChange={e => setUploadCategory(e.target.value)}
                  style={{ maxWidth: 180 }}
                >
                  {OFFER_DOC_CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleUpload}
                  disabled={uploading}
                  className={styles.fileInput}
                />
                <button
                  className={styles.btnPrimary}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {uploading ? 'Uploading…' : 'Upload Document'}
                </button>
              </div>
              <p className={styles.uploadHint}>Max 10 MB. Select a category then choose a file.</p>
            </div>
          )}

          {/* Document list */}
          {o.documents?.length || 0 === 0 ? (
            <p className={styles.emptyText}>No documents uploaded yet.</p>
          ) : (
            <table className={styles.docTable}>
              <thead>
                <tr>
                  <th>File</th>
                  <th>Category</th>
                  <th>Uploaded</th>
                  <th>By</th>
                  <th style={{ width: 90 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {o.documents?.map((doc: any) => (
                  <tr key={doc.offerDocumentId}>
                    <td className={styles.docFileName}>{doc.fileName}</td>
                    <td>
                      <span className={styles.categoryBadge}>{doc.category}</span>
                    </td>
                    <td>{formatDate(doc.uploadedAt)}</td>
                    <td>{doc.uploadedBy}</td>
                    <td>
                      <div className={styles.docActions}>
                        <button
                          className={styles.iconBtn}
                          onClick={() => handleDownload(doc)}
                          title="Download"
                        >
                          ↓
                        </button>
                        {canEdit && (
                          <>
                            {deleteConfirmId === doc.offerDocumentId ? (
                              <div className={styles.deleteConfirm}>
                                <button
                                  className={styles.iconBtnDanger}
                                  onClick={() => handleDeleteDocument(doc.offerDocumentId)}
                                  title="Confirm delete"
                                >
                                  ✓
                                </button>
                                <button
                                  className={styles.iconBtn}
                                  onClick={() => setDeleteConfirmId(null)}
                                  title="Cancel"
                                >
                                  ✕
                                </button>
                              </div>
                            ) : (
                              <button
                                className={styles.iconBtnDanger}
                                onClick={() => setDeleteConfirmId(doc.offerDocumentId)}
                                title="Delete"
                              >
                                🗑
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>


        {/* ─── Checklist Section (WS-4) ─── */}
        {!isCreateMode && checklistItems.length > 0 && (
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>
            Document Checklist
            <span style={{ fontSize: '13px', fontWeight: 400, color: '#6B7280', marginLeft: '8px' }}>
              {checklistItems.filter((ci: any) => ci.status === 'Verified' || ci.status === 'Waived').length}/{checklistItems.length} complete
            </span>
          </h2>
          {/* Progress bar */}
          <div style={{ background: '#E5E7EB', borderRadius: '4px', height: '8px', marginBottom: '16px', overflow: 'hidden' }}>
            <div style={{
              background: '#059669',
              height: '100%',
              width: `${checklistItems.length > 0 ? (checklistItems.filter((ci: any) => ci.status !== 'Required').length / checklistItems.length * 100) : 0}%`,
              borderRadius: '4px',
              transition: 'width 0.3s',
            }} />
          </div>
          <table className={styles.docTable}>
            <thead>
              <tr>
                <th>Document</th>
                <th>Category</th>
                <th>Required</th>
                <th>Status</th>
                <th>Linked File</th>
              </tr>
            </thead>
            <tbody>
              {checklistItems.map((ci: any) => {
                const statusColors: Record<string, { bg: string; color: string }> = {
                  Required: { bg: '#F3F4F6', color: '#374151' },
                  Uploaded: { bg: '#DBEAFE', color: '#1E40AF' },
                  Verified: { bg: '#D1FAE5', color: '#065F46' },
                  Waived: { bg: '#FEF3C7', color: '#92400E' },
                };
                const sc = statusColors[ci.status] || statusColors.Required;
                return (
                  <tr key={ci.progressId}>
                    <td style={{ fontWeight: 500 }}>{ci.documentName}</td>
                    <td><span className={styles.categoryBadge}>{ci.documentCategory}</span></td>
                    <td>{ci.isRequired ? '✓' : '—'}</td>
                    <td>
                      <span style={{ padding: '2px 10px', borderRadius: '10px', fontSize: '12px', fontWeight: 600, background: sc.bg, color: sc.color }}>
                        {ci.status}
                      </span>
                    </td>
                    <td style={{ color: '#6B7280', fontSize: '13px' }}>{ci.offerDocumentFileName || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        )}

        {/* ─── Timeline Card ─── */}
        <div className={styles.card}>
          <h2 className={styles.cardTitle}>Timeline</h2>
          {auditEvents.length === 0 ? (
            <p className={styles.emptyText}>No activity recorded yet.</p>
          ) : (
            <div className={styles.timeline}>
              {auditEvents.map(event => (
                <div key={event.auditEventId} className={styles.timelineItem}>
                  <div className={styles.timelineDot} />
                  <div className={styles.timelineContent}>
                    <div className={styles.timelineHeader}>
                      <span className={styles.timelineAction}>{event.action}</span>
                      <span className={styles.timelineDate}>{formatDateTime(event.timestamp)}</span>
                    </div>
                    <div className={styles.timelineUser}>{event.performedByDisplayName}</div>
                    {event.details && <div className={styles.timelineDetails}>{event.details}</div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
